// Private-bucket image handling. A stored image value (recipes.image /
// collections.image) is the object path INCLUDING its bucket plus a `?t=` version
// token, e.g. "recipe-images/<uid>/<id>.jpg?t=1699999999". Because the buckets are
// private, such a value must be turned into a short-lived signed URL to display.
// Local files, blob:/data:/file: and http(s) URLs are already renderable and pass
// through untouched.

const SIGNED_BUCKETS = ["recipe-images", "collection-images"];

// Display signed URLs last an hour (re-signed near expiry by the view layer). The
// cache-download variant only needs to outlive a single fetch.
export const SIGNED_URL_TTL_SECONDS = 3600;
export const SIGNED_URL_DOWNLOAD_TTL_SECONDS = 120;

// The stored value for a freshly-uploaded image: "<bucket>/<uid>/<id>.jpg?t=<now>".
// The `?t=` token changes on every (re)upload so other devices detect the new
// version and refresh their cache.
export function buildStoredImagePath(bucket: string, userId: string, id: string): string {
  return `${bucket}/${userId}/${id}.jpg?t=${Date.now()}`;
}

// Classify a stored value: returns { bucket, path } when it's a private-bucket
// object that must be signed to display, or null when it's already renderable
// (local file, blob:, data:, file:, or an http(s) URL — incl. a legacy public URL).
export function parseStoragePath(
  stored?: string | null,
): { bucket: string; path: string } | null {
  if (!stored) return null;
  if (/^(https?:|blob:|data:|file:)/i.test(stored)) return null;
  const withoutQuery = stored.split("?")[0];
  const slash = withoutQuery.indexOf("/");
  if (slash < 1) return null;
  const bucket = withoutQuery.slice(0, slash);
  const path = withoutQuery.slice(slash + 1);
  if (!SIGNED_BUCKETS.includes(bucket) || !path) return null;
  return { bucket, path };
}

// Minimal structural shape of the Supabase client's storage API — lets core sign
// without depending on @supabase/supabase-js (each app passes its own client).
export interface StorageSigner {
  storage: {
    from(bucket: string): {
      createSignedUrl(
        path: string,
        expiresIn: number,
      ): Promise<{ data: { signedUrl: string } | null; error: unknown }>;
    };
  };
}

// Resolve a stored value to something displayable: direct-renderable values pass
// through; storage paths are signed. Returns undefined when signing fails (caller
// shows a placeholder / retries).
export async function signedImageUrl(
  client: StorageSigner,
  stored: string | undefined | null,
  expiresIn: number = SIGNED_URL_TTL_SECONDS,
): Promise<string | undefined> {
  if (!stored) return undefined;
  const parsed = parseStoragePath(stored);
  if (!parsed) return stored; // already renderable
  const { data } = await client.storage
    .from(parsed.bucket)
    .createSignedUrl(parsed.path, expiresIn);
  return data?.signedUrl ?? undefined;
}
